import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    TextField,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Chip
} from '@mui/material';
import { Close, Description, PlayArrow, Visibility, VisibilityOff, Add, Refresh, Code, ViewList, InfoOutlined, CheckCircle, LinkOff, Launch } from '@mui/icons-material';
import { useTheme } from '../../context/ThemeContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import customToolsService from '../../services/customToolsService';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import ActionBuilder from './ActionBuilder';
import styles from './CreateCustomToolDialog.module.scss';

const PARAM_LOCATIONS = ['query', 'path', 'header', 'body'];
const PARAM_TYPES = ['string', 'integer', 'number', 'boolean'];

// Full auth_config key sets per auth type (the backend requires the complete set when auth_config is sent)
const OAUTH1_KEYS = ['consumer_key', 'consumer_secret', 'token_id', 'token_secret', 'realm'];
const OAUTH2_CC_KEYS = ['token_url', 'client_id', 'client_secret', 'client_assertion', 'private_key', 'scope'];
const OAUTH2_AUTH_KEYS = ['authorization_url', 'token_url', 'client_id', 'client_secret', 'scope'];
const NETSUITE_BASE_URL_PLACEHOLDER = 'https://11015386-sb1.suitetalk.api.netsuite.com/services/rest/record/v1';

// The ActionBuilder/preview renders parameters as a flat array of
// { name, type, location, required, description }. Incoming actions may instead
// carry parameters in JSON-Schema object form ({ type, properties, required }),
// e.g. pasted JSON or MCP tool schemas. Normalize an action's parameters to the
// array form so the preview can render them. Already-array params pass through.
const normalizeActionParameters = (action) => {
    const params = action?.parameters;

    if (Array.isArray(params)) return action;

    if (params && typeof params === 'object' && params.properties) {
        const properties = params.properties;
        const required = Array.isArray(params.required) ? params.required : [];
        const arr = Object.keys(properties).map((key) => ({
            name: key,
            type: properties[key]?.type || 'string',
            location: 'body', // JSON-Schema params carry no location; body is the safe default
            required: required.includes(key),
            description: properties[key]?.description || ''
        }));
        return { ...action, parameters: arr };
    }

    // Object without properties (empty schema) or missing/invalid → empty array
    return { ...action, parameters: [] };
};

const normalizeActions = (actions) =>
    Array.isArray(actions) ? actions.map(normalizeActionParameters) : [];

const updateUrlWithParam = (url, oldParam, newParam) => {
    let newUrl = url || '';

    // Remove old parameter
    if (oldParam && oldParam.name && (oldParam.location === 'path' || oldParam.location === 'query')) {
        if (oldParam.location === 'path') {
            newUrl = newUrl.replace(`{${oldParam.name}}`, '');
            newUrl = newUrl.replace(/([^:])\/\/+/g, '$1/');
        } else if (oldParam.location === 'query') {
            const regex1 = new RegExp(`\\?${oldParam.name}=([^&]*)`);
            const regex2 = new RegExp(`&${oldParam.name}=([^&]*)`);
            if (regex1.test(newUrl)) {
                newUrl = newUrl.replace(regex1, '?');
            } else if (regex2.test(newUrl)) {
                newUrl = newUrl.replace(regex2, '');
            }
            newUrl = newUrl.replace(/\?&/, '?').replace(/\?$/, '');
            newUrl = newUrl.replace(/([&?])&+/g, '$1');
        }
    }

    // Add new parameter
    if (newParam && newParam.name && (newParam.location === 'path' || newParam.location === 'query')) {
        if (newParam.location === 'path') {
            if (!newUrl.includes(`{${newParam.name}}`)) {
                newUrl = newUrl.endsWith('/') ? `${newUrl}{${newParam.name}}` : `${newUrl}/{${newParam.name}}`;
            }
        } else if (newParam.location === 'query') {
            const separator = newUrl.includes('?') ? '&' : '?';
            const val = newParam.value || '';
            newUrl = `${newUrl}${separator}${newParam.name}=${val}`;
        }
    }

    return newUrl;
};

const CreateCustomToolDialog = ({
    open,
    onClose,
    onRefresh,
    editTool = null,
}) => {
    const { isDarkMode } = useTheme();
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false); // initial edit-mode data fetch
    const [activeTab, setActiveTab] = useState(0);

    // Form State
    const [name, setName] = useState('');
    const [toolKey, setToolKey] = useState('');
    const [description, setDescription] = useState('');
    const [apiType, setApiType] = useState('REST'); // REST, GRAPHQL, MCP

    // REST/GraphQL Config
    const [baseUrl, setBaseUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [authType, setAuthType] = useState('API_KEY'); // API_KEY, BEARER_TOKEN, OAUTH2, BASIC_AUTH, NO_AUTH, OAUTH1, OAUTH2_CLIENT_CREDENTIALS, OAUTH2_AUTHORIZATION
    const [authHeaderName, setAuthHeaderName] = useState('X-API-Key');
    const [showApiKey, setShowApiKey] = useState(false);

    // auth_config for newer auth types (NetSuite TBA, OAuth2 Client Credentials, OAuth2 Authorization)
    const [authConfig, setAuthConfig] = useState({});
    const [showSecrets, setShowSecrets] = useState({}); // per-field visibility toggle
    const [showAdvancedOAuth, setShowAdvancedOAuth] = useState(false);
    const [authErrors, setAuthErrors] = useState({}); // per-field inline validation errors
    const [savedToolId, setSavedToolId] = useState(null);
    const [oauthActionLoading, setOauthActionLoading] = useState(false);
    const awaitingOAuthRef = useRef(false);
    const needsParentRefreshRef = useRef(false);

    const handleDialogClose = () => {
        awaitingOAuthRef.current = false;
        setSavedToolId(null);
        const shouldRefresh = needsParentRefreshRef.current;
        needsParentRefreshRef.current = false;
        onClose();
        if (shouldRefresh && onRefresh) {
            onRefresh();
        }
    };

    // MCP Config
    const [mcpServerUrl, setMcpServerUrl] = useState('');
    const [mcpTransport, setMcpTransport] = useState('http'); // 'http' or 'stdio'
    const [mcpSupportsDcr, setMcpSupportsDcr] = useState(null);
    const [dcrChecking, setDcrChecking] = useState(false);
    const [dcrLoading, setDcrLoading] = useState(false);
    const [mcpCommand, setMcpCommand] = useState('');
    const [mcpArgs, setMcpArgs] = useState([]);
    const [mcpEnv, setMcpEnv] = useState([]); // Array of { key, value }
    const [mcpHeaders, setMcpHeaders] = useState([]); // Array of { key, value }
    const [parameters, setParameters] = useState([]); // Array of param objects

    const debouncedMcpServerUrl = useDebouncedValue(mcpServerUrl, 500);

    const checkDcrSupportForUrl = useCallback(async (url) => {
        const trimmed = (url || '').trim();
        if (!trimmed || !trimmed.startsWith('http')) {
            setMcpSupportsDcr(null);
            return;
        }
        setDcrChecking(true);
        try {
            const res = await customToolsService.checkMcpDcrSupport(trimmed);
            if (res && res.supports_dcr) {
                setMcpSupportsDcr(true);
                setMcpTransport('http');
            } else {
                setMcpSupportsDcr(false);
            }
        } catch (err) {
            console.warn('Error checking MCP DCR support:', err);
            setMcpSupportsDcr(false);
        } finally {
            setDcrChecking(false);
        }
    }, []);

    const handleMcpServerUrlChange = (newUrl) => {
        setMcpServerUrl(newUrl);
        const trimmed = (newUrl || '').trim();
        if (!trimmed) {
            setMcpSupportsDcr(null);
            return;
        }
        // When MCP Server URL is modified, reset DCR support status immediately so UI updates
        setMcpSupportsDcr(null);
        // Clear any old DCR credentials that were registered for a previous server
        setAuthConfig((prev) => {
            if (!prev?.is_dcr && !prev?.client_id && !prev?.authorization_url) return prev;
            const cleaned = { ...prev };
            delete cleaned.authorization_url;
            delete cleaned.token_url;
            delete cleaned.registration_endpoint;
            delete cleaned.authorization_server;
            delete cleaned.client_id;
            delete cleaned.client_secret;
            delete cleaned.scope;
            delete cleaned.resource;
            delete cleaned.token_endpoint_auth_method;
            delete cleaned.redirect_uri;
            delete cleaned.is_dcr;
            delete cleaned.connected_at;
            delete cleaned.access_token;
            delete cleaned.refresh_token;
            return cleaned;
        });
    };

    // Actions / JSON Config
    const [actionsJson, setActionsJson] = useState('[]'); // JSON string for actions array
    const [useActionBuilder, setUseActionBuilder] = useState(true);
    const [parsedActions, setParsedActions] = useState([]); // For ActionBuilder

    // Test State
    const [testInput, setTestInput] = useState('{}');
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);

    // MCP Fetch State
    const [fetchLoading, setFetchLoading] = useState(false);

    // Toast/Snackbar state
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
    const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

    const muiTheme = createTheme({
        palette: {
            mode: isDarkMode ? 'dark' : 'light',
            primary: {
                main: '#e36250',
            },
        },
    });

    const [initialApiKey, setInitialApiKey] = useState('');

    useEffect(() => {
        const fetchToolData = async () => {
            if (open) {
                setTestResult(null);
                setActiveTab(0);
                setOauthActionLoading(false);
                awaitingOAuthRef.current = false;
                needsParentRefreshRef.current = false;

                if (editTool) {
                    setSavedToolId(null);
                    setFetchingData(true);
                    try {
                        // Fetch fresh definition and config
                        const [toolDef, toolConfig] = await Promise.all([
                            customToolsService.getCustomToolById(editTool.id),
                            customToolsService.getToolConfig(editTool.id).catch(err => {
                                console.warn('Failed to fetch config, using default', err);
                                return {};
                            })
                        ]);

                        // Populate form with refreshed data
                        setName(toolDef.name || '');
                        setToolKey(toolDef.tool_key || '');
                        setDescription(toolDef.description || '');
                        setApiType(toolDef.api_type || 'REST');
                        setParameters(toolDef.parameters || []);
                        setMcpServerUrl(toolDef.mcp_server_url || '');

                        // MCP Config
                        const mcpConfig = toolDef.mcp_config || {};
                        setMcpTransport(mcpConfig.transport || 'http');
                        if (mcpConfig.transport === 'stdio') {
                            const cmdArr = mcpConfig.command || [];
                            if (cmdArr.length > 0) {
                                setMcpCommand(cmdArr[0]);
                                setMcpArgs(cmdArr.slice(1));
                            }
                            const envArr = Object.entries(mcpConfig.env || {}).map(([key, value]) => ({ key, value }));
                            setMcpEnv(envArr);
                        } else if (mcpConfig.transport === 'http') {
                            const headersArr = Object.entries(mcpConfig.http_headers || {}).map(([key, value]) => ({ key, value }));

                            setMcpHeaders(headersArr);
                        }

                        // Use config data if available, fallback to def
                        setBaseUrl(toolConfig.base_url || toolDef.base_url || '');
                        // Normalize legacy NETSUITE_TBA → OAUTH1 (backend treats them as aliases)
                        const loadedAuthType = toolConfig.auth_type || toolDef.auth_type || 'API_KEY';
                        setAuthType(loadedAuthType === 'NETSUITE_TBA' ? 'OAUTH1' : loadedAuthType);

                        if ((toolDef.api_type || '').toUpperCase() === 'MCP') {
                            const isOAuth = loadedAuthType === 'OAUTH2_AUTHORIZATION' || Boolean(toolDef?.auth_config?.is_dcr);
                            setMcpSupportsDcr(isOAuth ? true : false);
                        } else {
                            setMcpSupportsDcr(null);
                        }

                        // Merge both sources, letting the definition win — it returns the full
                        // credential values (the config endpoint may mask them), while still
                        // preserving any config-only keys (e.g. header_name for API_KEY).
                        // Also inspect toolDef.auth_config.connected_at directly on the GET /api/v1/custom-tools/{tool_id} response.
                        const fetchedAuthConfig = {
                            ...(toolConfig.auth_config || {}),
                            ...(toolDef.auth_config || {}),
                            connected_at: toolDef?.auth_config?.connected_at ?? null,
                        };
                        if (fetchedAuthConfig.header_name) {
                            setAuthHeaderName(fetchedAuthConfig.header_name);
                        }
                        setAuthConfig(fetchedAuthConfig);
                        setShowSecrets({});
                        setShowAdvancedOAuth(Boolean(fetchedAuthConfig.client_assertion || fetchedAuthConfig.private_key));
                        setAuthErrors({});

                        // Handle masked API Key
                        let fetchedKey = '';
                        if (toolConfig.api_key) {
                            fetchedKey = toolConfig.api_key;
                        } else if (toolDef.api_key) {
                            fetchedKey = toolDef.api_key;
                        }
                        setApiKey(fetchedKey);
                        setInitialApiKey(fetchedKey);

                        const actionsVal = toolDef.actions || [];
                        setParsedActions(normalizeActions(actionsVal));
                        setActionsJson(JSON.stringify(actionsVal, null, 2));

                    } catch (err) {
                        console.error('Error fetching tool details:', err);
                        showToast('Failed to load tool details', 'error');
                    } finally {
                        setSavedToolId(editTool.id);
                        setFetchingData(false);
                    }
                } else {
                    // Defaults for new tool
                    setSavedToolId(null);
                    setName('');
                    setToolKey('');
                    setDescription('');
                    setApiType('REST');
                    setParameters([]);
                    setBaseUrl('');
                    setApiKey('');
                    setInitialApiKey('');
                    setAuthType('API_KEY');
                    setAuthHeaderName('X-API-Key');
                    setAuthConfig({});
                    setShowSecrets({});
                    setShowAdvancedOAuth(false);
                    setAuthErrors({});

                    // MCP Defaults
                    setMcpServerUrl('');
                    setMcpTransport('http');
                    setMcpSupportsDcr(null);
                    setDcrChecking(false);
                    setDcrLoading(false);
                    setMcpCommand('');
                    setMcpArgs([]);
                    setMcpEnv([]);
                    setMcpHeaders([]);

                    setActionsJson('[]');
                    setParsedActions([]);
                    setUseActionBuilder(true);
                }
            }
        };

        fetchToolData();
    }, [open, editTool]);

    const openOAuthPopup = (url) => {
        const width = 600;
        const height = 700;
        const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
        const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
        return window.open(
            url,
            'custom_tool_oauth_popup',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,status=1`
        );
    };

    // Refresh tool.auth_config.connected_at via postMessage from popup or when returning from an active OAuth authorization tab.
    useEffect(() => {
        if (!open || !savedToolId || (authType !== 'OAUTH2_AUTHORIZATION' && !mcpSupportsDcr)) {
            return;
        }
        let cancelled = false;

        const handleOAuthMessage = (event) => {
            if (event.data?.type === 'CUSTOM_TOOL_OAUTH_STATUS') {
                const { status, tool_id, error } = event.data;
                if (tool_id && String(tool_id) !== String(savedToolId)) {
                    return;
                }
                awaitingOAuthRef.current = false;
                if (status === 'connected') {
                    customToolsService.getCustomToolById(savedToolId)
                        .then((toolDef) => {
                            if (!cancelled && toolDef) {
                                if (toolDef.auth_config) {
                                    setAuthConfig((prev) => ({
                                        ...prev,
                                        connected_at: toolDef.auth_config?.connected_at || new Date().toISOString(),
                                    }));
                                }
                                if (toolDef.actions && toolDef.actions.length > 0) {
                                    const stdTools = normalizeActions(toolDef.actions);
                                    setParsedActions(stdTools);
                                    setActionsJson(JSON.stringify(stdTools, null, 2));
                                }
                                needsParentRefreshRef.current = true;
                                showToast('OAuth connected successfully!', 'success');
                            }
                        })
                        .catch(() => {
                            setAuthConfig((prev) => ({
                                ...prev,
                                connected_at: new Date().toISOString(),
                            }));
                            needsParentRefreshRef.current = true;
                            showToast('OAuth connected successfully!', 'success');
                        });
                } else if (status === 'error') {
                    showToast(error || 'OAuth connection failed or was cancelled', 'error');
                }
            }
        };

        const handleWindowFocus = () => {
            if (!awaitingOAuthRef.current) {
                return;
            }
            awaitingOAuthRef.current = false;
            customToolsService.getCustomToolById(savedToolId)
                .then((toolDef) => {
                    if (!cancelled && toolDef) {
                        if (toolDef.auth_config) {
                            setAuthConfig((prev) => ({
                                ...prev,
                                connected_at: toolDef.auth_config?.connected_at ?? null,
                            }));
                            if (toolDef.auth_config?.connected_at) {
                                needsParentRefreshRef.current = true;
                            }
                        }
                        if (toolDef.actions && toolDef.actions.length > 0) {
                            const stdTools = normalizeActions(toolDef.actions);
                            setParsedActions(stdTools);
                            setActionsJson(JSON.stringify(stdTools, null, 2));
                        }
                    }
                })
                .catch(() => {});
        };

        window.addEventListener('message', handleOAuthMessage);
        window.addEventListener('focus', handleWindowFocus);
        return () => {
            cancelled = true;
            window.removeEventListener('message', handleOAuthMessage);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [open, authType, savedToolId, mcpSupportsDcr]);

    // Auto-probe MCP server URL to determine if it supports DCR OAuth
    useEffect(() => {
        if (apiType === 'MCP' && open && !fetchingData) {
            if (debouncedMcpServerUrl && debouncedMcpServerUrl.trim().startsWith('http')) {
                checkDcrSupportForUrl(debouncedMcpServerUrl);
            } else if (!debouncedMcpServerUrl || !debouncedMcpServerUrl.trim()) {
                if (!editTool) {
                    setMcpSupportsDcr(null);
                }
            }
        }
    }, [apiType, open, fetchingData, debouncedMcpServerUrl, checkDcrSupportForUrl, editTool]);

    const handleAddParameter = () => {
        setParameters(prev => [...prev, {
            name: 'new_param',
            value: '',
            type: 'string',
            location: 'query',
            required: false,
            description: ''
        }]);
    };

    const handleParameterChange = (index, field, value) => {
        const updatedParams = [...parameters];
        const oldParam = { ...updatedParams[index] };
        updatedParams[index] = { ...oldParam, [field]: value };
        const newParam = updatedParams[index];

        const isUrlAffecting = field === 'name' || field === 'location' || field === 'value';

        if (isUrlAffecting) {
            if (apiType === 'MCP') {
                setMcpServerUrl(prev => updateUrlWithParam(prev, oldParam, newParam));
            } else {
                setBaseUrl(prev => updateUrlWithParam(prev, oldParam, newParam));
            }
        }

        setParameters(updatedParams);
    };

    const handleDeleteParameter = (index) => {
        const paramToDelete = parameters[index];
        if (paramToDelete) {
            if (apiType === 'MCP') {
                setMcpServerUrl(prev => updateUrlWithParam(prev, paramToDelete, null));
            } else {
                setBaseUrl(prev => updateUrlWithParam(prev, paramToDelete, null));
            }
        }
        setParameters(prev => prev.filter((_, i) => i !== index));
    };

    const handleUrlBlur = (url) => {
        if (!url) return;
        let newParams = [...parameters];
        let changed = false;

        // Path params
        const pathMatches = url.match(/\{([^}]+)\}/g);
        if (pathMatches) {
            pathMatches.forEach(match => {
                const paramName = match.slice(1, -1);
                if (!newParams.some(p => p.name === paramName && p.location === 'path')) {
                    newParams.push({
                        name: paramName,
                        value: '',
                        type: 'string',
                        location: 'path',
                        required: true,
                        description: ''
                    });
                    changed = true;
                }
            });
        }

        // Query params
        try {
            const urlObj = new URL(url.startsWith('http') ? url : `http://dummy.com${url.startsWith('/') ? '' : '/'}${url}`);
            urlObj.searchParams.forEach((val, key) => {
                if (!newParams.some(p => p.name === key && p.location === 'query')) {
                    newParams.push({
                        name: key,
                        value: val,
                        type: 'string',
                        location: 'query',
                        required: false,
                        description: ''
                    });
                    changed = true;
                }
            });
        } catch (e) {
            // Ignore invalid URLs
        }

        if (changed) {
            setParameters(newParams);
        }
    };


    const handleFetchMcpTools = async () => {
        setFetchLoading(true);
        try {
            const payload = {};
            if (mcpSupportsDcr) {
                if (!mcpServerUrl) throw new Error('MCP Server URL is required to fetch tools');
                payload.mcp_server_url = mcpServerUrl;
                payload.auth_type = 'OAUTH2_AUTHORIZATION';
                payload.auth_config = authConfig;
                const currentId = savedToolId || editTool?.id;
                if (currentId) {
                    payload.tool_id = currentId;
                }
            } else if (mcpTransport === 'http') {
                if (!mcpServerUrl) throw new Error('MCP Server URL is required to fetch tools');
                payload.mcp_server_url = mcpServerUrl;
                payload.auth_type = 'BEARER_TOKEN';
                const headersObj = {};
                mcpHeaders.forEach(item => {
                    if (item.key && item.key.toLowerCase() !== 'accept') headersObj[item.key] = item.value;
                });
                if (Object.keys(headersObj).length > 0) {
                    payload.mcp_config = { transport: 'http', http_headers: headersObj };
                }
            } else {
                if (!mcpCommand) throw new Error('Command is required for Stdio transport');
                const fullCommand = [mcpCommand, ...mcpArgs.filter(arg => arg.trim() !== '')];
                const envObj = {};
                mcpEnv.forEach(item => {
                    if (item.key) envObj[item.key] = item.value;
                });
                payload.mcp_config = {
                    transport: 'stdio',
                    command: fullCommand,
                    env: envObj
                };
            }

            const tools = await customToolsService.fetchMcpTools(payload);

            // Transform fetched tools to actions format if needed, or use directly
            // Assuming response is strict list of actions as per user request description
            // If response wraps tools, we might need mapping.
            // Based on user prompt: "Populate the actions list for the custom tool with these discovered tools"

            if (tools && Array.isArray(tools)) {
                const standardizedTools = normalizeActions(tools);
                setParsedActions(standardizedTools);
                setActionsJson(JSON.stringify(standardizedTools, null, 2));
                setUseActionBuilder(true);
                showToast('Tools fetched successfully!', 'success');
            } else {
                throw new Error('Invalid response format from server');
            }

        } catch (err) {
            console.error('Error fetching MCP tools:', err);
            showToast(err.response?.data?.detail || err.message || 'Failed to fetch tools', 'error');
        } finally {
            setFetchLoading(false);
        }
    };

    const handleAuthConfigChange = (key, value) => {
        setAuthConfig(prev => ({ ...prev, [key]: value }));
        if (authErrors[key]) {
            setAuthErrors(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    // Build the complete auth_config payload. The backend requires the full credential
    // set whenever auth_config is sent, so every non-empty key is included.
    const buildAuthConfigPayload = (keys) => {
        const cfg = {};
        keys.forEach((key) => {
            const val = authConfig[key];
            if (val !== undefined && val !== '') {
                cfg[key] = val;
            }
        });
        return cfg;
    };

    const isOAuth2AuthFilled = OAUTH2_AUTH_KEYS.every(
        (key) => Boolean(authConfig[key] && String(authConfig[key]).trim())
    );
    const isOAuthConnected = Boolean(authConfig?.connected_at);
    const isDcrRegistered = Boolean(authConfig?.client_id && authConfig?.authorization_url);

    const buildToolDataPayload = () => {
        if (!name) throw new Error('Name is required');
        if (!toolKey) throw new Error('Tool Key is required');

        let finalActions = [];
        if (useActionBuilder) {
            finalActions = parsedActions;
        } else {
            try {
                finalActions = JSON.parse(actionsJson);
                if (!Array.isArray(finalActions)) {
                    throw new Error('Actions must be an array');
                }
            } catch (e) {
                throw new Error('Invalid JSON format in Actions definition');
            }
        }

        const toolData = {
            name,
            tool_key: toolKey,
            description,
            api_type: apiType,
            parameters,
            actions: finalActions,
        };

        if (apiType === 'MCP') {
            if (mcpSupportsDcr) {
                if (!mcpServerUrl) throw new Error('MCP Server URL is required');
                toolData.mcp_server_url = mcpServerUrl;
                toolData.auth_type = 'OAUTH2_AUTHORIZATION';
                toolData.mcp_config = { transport: 'http' };
                if (authConfig && Object.keys(authConfig).length > 0) {
                    toolData.auth_config = authConfig;
                }
            } else if (mcpTransport === 'http') {
                if (!mcpServerUrl) throw new Error('MCP Server URL is required');
                toolData.mcp_server_url = mcpServerUrl;
                toolData.auth_type = 'BEARER_TOKEN';
                const headersObj = {};
                mcpHeaders.forEach(item => {
                    if (item.key && item.key.toLowerCase() !== 'accept') headersObj[item.key] = item.value;
                });
                toolData.mcp_config = {
                    transport: 'http',
                    ...(Object.keys(headersObj).length > 0 && { http_headers: headersObj })
                };
            } else {
                if (!mcpCommand) throw new Error('Command is required for Stdio transport');
                const fullCommand = [mcpCommand, ...mcpArgs.filter(arg => arg.trim() !== '')];

                const envObj = {};
                mcpEnv.forEach(item => {
                    if (item.key) envObj[item.key] = item.value;
                });

                toolData.mcp_config = {
                    transport: 'stdio',
                    command: fullCommand,
                    env: envObj
                };
            }

            // Only send API key if it has changed from the initial (masked) value
            if (apiKey && apiKey !== initialApiKey) {
                toolData.api_key = apiKey;
            }
        } else {
            // REST / GraphQL
            if (!baseUrl) throw new Error('Base URL is required');
            toolData.base_url = baseUrl;
            toolData.auth_type = authType;

            if (authType === 'OAUTH1') {
                // OAuth 1.0a carries no api_key — all credentials live in auth_config,
                // and the backend requires the complete set on every save.
                const required = ['consumer_key', 'consumer_secret', 'token_id', 'token_secret', 'realm'];
                const errs = {};
                required.forEach((key) => {
                    if (!authConfig[key]) errs[key] = 'Required';
                });
                if (Object.keys(errs).length > 0) {
                    setAuthErrors(errs);
                    throw new Error('Please fill all required OAuth 1.0a fields');
                }
                toolData.auth_config = buildAuthConfigPayload(OAUTH1_KEYS);
            } else if (authType === 'OAUTH2_CLIENT_CREDENTIALS') {
                const errs = {};
                if (!authConfig.token_url) errs.token_url = 'Required';
                if (!authConfig.client_id) errs.client_id = 'Required';
                const hasCredential = Boolean(authConfig.client_secret || authConfig.client_assertion || authConfig.private_key);
                if (!hasCredential) {
                    errs.client_secret = 'Provide a Client Secret, Client Assertion, or Private Key';
                }
                if (Object.keys(errs).length > 0) {
                    setAuthErrors(errs);
                    throw new Error('Please provide Token URL, Client ID, and at least one credential');
                }
                toolData.auth_config = buildAuthConfigPayload(OAUTH2_CC_KEYS);
            } else if (authType === 'OAUTH2_AUTHORIZATION') {
                const errs = {};
                OAUTH2_AUTH_KEYS.forEach((key) => {
                    if (!authConfig[key] || !String(authConfig[key]).trim()) {
                        errs[key] = 'Required';
                    }
                });
                if (Object.keys(errs).length > 0) {
                    setAuthErrors(errs);
                    throw new Error('Please fill all required OAuth2 Authorization fields');
                }
                const oauthAuthPayload = buildAuthConfigPayload(OAUTH2_AUTH_KEYS);
                if (authConfig.connected_at) {
                    oauthAuthPayload.connected_at = authConfig.connected_at;
                }
                toolData.auth_config = oauthAuthPayload;
            } else {
                if (authType !== 'NO_AUTH') {
                    // Only send API key if it has changed from the initial (masked) value
                    // This prevents overwriting the real key with "********"
                    if (apiKey && apiKey !== initialApiKey) {
                        toolData.api_key = apiKey;
                    } else if (!editTool && !apiKey && authType !== 'OAUTH2') {
                        // For new tools, if auth is required but not provided (and not oauth)
                        // This is a loose check, backend will validate too
                    }
                }

                if (authType === 'API_KEY') {
                    toolData.auth_config = { header_name: authHeaderName };
                }
            }
        }

        return toolData;
    };

    const persistToolDefinition = async () => {
        const toolData = buildToolDataPayload();
        const currentToolId = savedToolId || editTool?.id;
        if (currentToolId) {
            await customToolsService.updateTool(currentToolId, toolData);
            return currentToolId;
        }
        const created = await customToolsService.createTool(toolData);
        const newId = created?.id || created?.tool_id;
        if (newId) {
            setSavedToolId(newId);
        }
        return newId;
    };

    const handleSave = async () => {
        setLoading(true);

        try {
            const currentToolId = savedToolId || editTool?.id;
            await persistToolDefinition();
            showToast(currentToolId ? 'Tool updated successfully!' : 'Tool created successfully!', 'success');

            setTimeout(() => {
                if (onRefresh) onRefresh();
                onClose();
            }, 1000);
        } catch (err) {
            console.error('Error saving tool:', err);
            showToast(err.response?.data?.detail || err.message || 'Failed to save tool', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleConnectAppDcr = async () => {
        if (!mcpServerUrl) {
            showToast('Please provide an MCP Server URL first', 'error');
            return;
        }
        setDcrLoading(true);
        try {
            let currentToolId = savedToolId || editTool?.id;
            if (!currentToolId) {
                currentToolId = await persistToolDefinition();
            }

            const dcrRes = await customToolsService.mcpDcrDiscover({
                mcp_server_url: mcpServerUrl,
                tool_id: currentToolId,
            });

            const updatedConfig = {
                ...authConfig,
                authorization_url: dcrRes.authorization_url,
                token_url: dcrRes.token_url,
                registration_endpoint: dcrRes.registration_endpoint,
                authorization_server: dcrRes.authorization_server,
                client_id: dcrRes.client_id,
                client_secret: dcrRes.client_secret,
                scope: dcrRes.scope,
                resource: dcrRes.resource,
                token_endpoint_auth_method: dcrRes.token_endpoint_auth_method,
                is_dcr: true,
                pkce_enabled: true,
            };
            setAuthConfig(updatedConfig);
            setAuthType('OAUTH2_AUTHORIZATION');
            setMcpSupportsDcr(true);
            showToast('MCP Client registered successfully! Click "Connect with OAuth" to continue.', 'success');
        } catch (err) {
            console.error('Error during MCP DCR discovery:', err);
            showToast(err.response?.data?.detail || err.message || 'Failed to connect app via DCR', 'error');
        } finally {
            setDcrLoading(false);
        }
    };

    const handleConnectOAuth = async () => {
        setOauthActionLoading(true);
        try {
            const toolId = await persistToolDefinition();
            if (!toolId) {
                throw new Error('Please save the tool first before connecting OAuth');
            }
            needsParentRefreshRef.current = true;

            const frontendRedirectUri = `${window.location.origin}/configure/tools`;
            const authRes = await customToolsService.authorizeOAuth(toolId, frontendRedirectUri);
            const authUrl =
                authRes?.authorization_url ||
                authRes?.auth_url ||
                authRes?.url ||
                authRes?.redirect_url ||
                (typeof authRes === 'string' ? authRes : '');

            if (!authUrl) {
                throw new Error('Failed to obtain OAuth authorization URL');
            }

            awaitingOAuthRef.current = true;
            openOAuthPopup(authUrl);
        } catch (err) {
            console.error('Error connecting with OAuth:', err);
            showToast(err.response?.data?.detail || err.message || 'Failed to initiate OAuth connection', 'error');
        } finally {
            setOauthActionLoading(false);
        }
    };

    const handleReconnectOAuth = async () => {
        setOauthActionLoading(true);
        try {
            const toolId = await persistToolDefinition();
            if (!toolId) {
                throw new Error('Tool ID is required to reconnect OAuth');
            }
            needsParentRefreshRef.current = true;

            const frontendRedirectUri = `${window.location.origin}/configure/tools`;
            const authRes = await customToolsService.authorizeOAuth(toolId, frontendRedirectUri);
            const authUrl =
                authRes?.authorization_url ||
                authRes?.auth_url ||
                authRes?.url ||
                authRes?.redirect_url ||
                (typeof authRes === 'string' ? authRes : '');

            if (!authUrl) {
                throw new Error('Failed to obtain OAuth authorization URL');
            }

            awaitingOAuthRef.current = true;
            openOAuthPopup(authUrl);
        } catch (err) {
            console.error('Error reconnecting OAuth:', err);
            showToast(err.response?.data?.detail || err.message || 'Failed to reconnect OAuth', 'error');
        } finally {
            setOauthActionLoading(false);
        }
    };

    const handleDisconnectOAuth = async () => {
        const currentToolId = savedToolId || editTool?.id;
        if (!currentToolId) return;

        setOauthActionLoading(true);
        try {
            await customToolsService.disconnectOAuth(currentToolId);
            // Re-fetch GET /api/v1/custom-tools/{tool_id} and inspect tool.auth_config.connected_at directly
            const refreshedDef = await customToolsService.getCustomToolById(currentToolId).catch(() => null);
            setAuthConfig(prev => ({
                ...prev,
                ...(refreshedDef?.auth_config || {}),
                connected_at: refreshedDef?.auth_config?.connected_at ?? null,
            }));
            needsParentRefreshRef.current = true;
            showToast('OAuth disconnected successfully', 'success');
        } catch (err) {
            console.error('Error disconnecting OAuth:', err);
            showToast(err.response?.data?.detail || err.message || 'Failed to disconnect OAuth', 'error');
        } finally {
            setOauthActionLoading(false);
        }
    };

    const handleTest = async () => {
        setTestLoading(true);
        setTestResult(null);

        try {
            let parsedInput;
            try {
                parsedInput = JSON.parse(testInput);
            } catch (e) {
                throw new Error('Invalid JSON in Test Input');
            }

            const currentToolId = savedToolId || editTool?.id;
            if (!currentToolId) {
                throw new Error('Please save the tool before testing');
            }

            const result = await customToolsService.testTool(currentToolId, parsedInput);
            setTestResult(result);
            showToast('Test executed successfully', 'success');
        } catch (err) {
            console.error('Test failed:', err);
            showToast(err.response?.data?.detail || err.message || 'Test failed', 'error');
        } finally {
            setTestLoading(false);
        }
    };

    const renderAuthConfigField = (key, label, { required = false, secret = false, helperText = '', placeholder = '', multiline = false } = {}) => (
        <TextField
            fullWidth
            label={required ? `${label} *` : label}
            value={authConfig[key] || ''}
            onChange={(e) => handleAuthConfigChange(key, e.target.value)}
            type={secret && !showSecrets[key] ? 'password' : 'text'}
            error={Boolean(authErrors[key])}
            helperText={authErrors[key] || helperText}
            placeholder={placeholder}
            multiline={multiline}
            rows={multiline ? 4 : undefined}
            InputProps={secret ? {
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={() => setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }))}
                            edge="end"
                        >
                            {showSecrets[key] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                ),
            } : undefined}
            sx={{ mb: 2 }}
        />
    );

    return (
        <ThemeProvider theme={muiTheme}>
            <Dialog
                open={open}
                onClose={handleDialogClose}
                maxWidth="lg"
                fullWidth
                className={styles.dialog}
                sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
            >
                <DialogTitle className={styles.dialogTitle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" component="span">
                            {editTool ? 'Edit Custom Tool' : 'Create Custom Tool'}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleDialogClose} size="small" className={styles.closeButton}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={styles.dialogContent}>
                    {fetchingData && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                height: '100%',
                                minHeight: 300,
                            }}
                        >
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Loading tool details…
                            </Typography>
                        </Box>
                    )}

                    {!fetchingData && editTool && (
                        <Tabs
                            value={activeTab}
                            onChange={(e, val) => setActiveTab(val)}
                            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab icon={<Description />} label="Definition" />
                            <Tab icon={<PlayArrow />} label="Test" />
                        </Tabs>
                    )}

                    {!fetchingData && activeTab === 0 && (
                        <Box sx={{ display: 'flex', gap: 4, height: '100%' }}>
                            {/* Left Column - General & Config */}
                            <Box sx={{ flex: 1, overflow: 'auto', pr: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>General Information</Typography>

                                <TextField
                                    fullWidth
                                    label="Name *"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    sx={{ mb: 2 }}
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Unique Key *"
                                    value={toolKey}
                                    onChange={(e) => setToolKey(e.target.value)}
                                    placeholder="my_custom_tool"
                                    helperText="Unique identifier (alphanumeric, underscores)"
                                    sx={{ mb: 2 }}
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    multiline
                                    rows={2}
                                    sx={{ mb: 3 }}
                                />

                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Configuration</Typography>

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>API Type</InputLabel>
                                    <Select
                                        value={apiType}
                                        label="API Type"
                                        onChange={(e) => setApiType(e.target.value)}
                                    >
                                        <MenuItem value="REST">REST API</MenuItem>
                                        <MenuItem value="GRAPHQL">GraphQL API</MenuItem>
                                        <MenuItem value="MCP">MCP Server</MenuItem>
                                    </Select>
                                </FormControl>

                                {apiType !== 'MCP' ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            label="Base URL *"
                                            value={baseUrl}
                                            onChange={(e) => setBaseUrl(e.target.value)}
                                            onBlur={(e) => handleUrlBlur(e.target.value)}
                                            placeholder={authType === 'OAUTH1' ? NETSUITE_BASE_URL_PLACEHOLDER : 'https://api.example.com/v1'}
                                            sx={{ mb: 3 }}
                                            required
                                        />

                                        <FormControl fullWidth sx={{ mb: 2 }}>
                                            <InputLabel>Authentication Type</InputLabel>
                                            <Select
                                                value={authType}
                                                label="Authentication Type"
                                                onChange={(e) => setAuthType(e.target.value)}
                                            >
                                                <MenuItem value="NO_AUTH">No Authentication</MenuItem>
                                                <MenuItem value="API_KEY">API Key</MenuItem>
                                                <MenuItem value="BEARER_TOKEN">Bearer Token</MenuItem>
                                                <MenuItem value="BASIC_AUTH">Basic Auth</MenuItem>
                                                <MenuItem value="OAUTH2">OAuth2 — Static Token</MenuItem>
                                                <MenuItem value="OAUTH1">OAuth 1.0a (HMAC-SHA256) — e.g. NetSuite TBA</MenuItem>
                                                <MenuItem value="OAUTH2_CLIENT_CREDENTIALS">OAuth2 — Client Credentials (M2M)</MenuItem>
                                                <MenuItem value="OAUTH2_AUTHORIZATION">OAuth2 — Authorization Code</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {authType === 'API_KEY' && (
                                            <TextField
                                                fullWidth
                                                label="Header Name"
                                                value={authHeaderName}
                                                onChange={(e) => setAuthHeaderName(e.target.value)}
                                                placeholder="X-API-Key"
                                                sx={{ mb: 2 }}
                                            />
                                        )}

                                        {authType !== 'NO_AUTH' && authType !== 'OAUTH2' && authType !== 'OAUTH1' && authType !== 'OAUTH2_CLIENT_CREDENTIALS' && authType !== 'OAUTH2_AUTHORIZATION' && (
                                            <TextField
                                                fullWidth
                                                label={authType === 'BEARER_TOKEN' ? 'Token' : 'API Key / Secret'}
                                                value={apiKey}
                                                onChange={(e) => setApiKey(e.target.value)}
                                                type={showApiKey ? 'text' : 'password'}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowApiKey(!showApiKey)}
                                                                edge="end"
                                                            >
                                                                {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ mb: 2 }}
                                            />
                                        )}

                                        {authType === 'OAUTH1' && (
                                            <>
                                                {renderAuthConfigField('consumer_key', 'Consumer Key', { required: true })}
                                                {renderAuthConfigField('consumer_secret', 'Consumer Secret', { required: true, secret: true })}
                                                {renderAuthConfigField('token_id', 'Token ID', { required: true })}
                                                {renderAuthConfigField('token_secret', 'Token Secret', { required: true, secret: true })}
                                                {renderAuthConfigField('realm', 'Account ID (Realm)', {
                                                    required: true,
                                                    helperText: 'UPPERCASE underscore form, e.g. 11015386_SB1. The Base URL host uses the lowercase-hyphen form, e.g. 11015386-sb1.'
                                                })}
                                            </>
                                        )}

                                        {authType === 'OAUTH2_CLIENT_CREDENTIALS' && (
                                            <>
                                                {renderAuthConfigField('token_url', 'Token URL', { required: true, placeholder: 'https://.../token' })}
                                                {renderAuthConfigField('client_id', 'Client ID', { required: true })}
                                                {renderAuthConfigField('client_secret', 'Client Secret', {
                                                    secret: true,
                                                    helperText: 'Provide at least one: Client Secret, Client Assertion, or Private Key.'
                                                })}
                                                {renderAuthConfigField('scope', 'Scope', { placeholder: 'optional' })}
                                                <Button
                                                    size="small"
                                                    onClick={() => setShowAdvancedOAuth(v => !v)}
                                                    sx={{ mb: 2, textTransform: 'none' }}
                                                >
                                                    {showAdvancedOAuth ? 'Hide Advanced' : 'Advanced (JWT / Private Key)'}
                                                </Button>
                                                {showAdvancedOAuth && (
                                                    <>
                                                        {renderAuthConfigField('client_assertion', 'Client Assertion (JWT)', { secret: true })}
                                                        {renderAuthConfigField('private_key', 'Private Key (PEM)', { secret: true, multiline: true })}
                                                    </>
                                                )}
                                            </>
                                        )}

                                        {authType === 'OAUTH2_AUTHORIZATION' && (
                                            <>
                                                {renderAuthConfigField('authorization_url', 'Authorization URL', { required: true, placeholder: 'https://.../authorize' })}
                                                {renderAuthConfigField('token_url', 'Token URL', { required: true, placeholder: 'https://.../token' })}
                                                {renderAuthConfigField('client_id', 'Client ID', { required: true })}
                                                {renderAuthConfigField('client_secret', 'Client Secret', { required: true, secret: true })}
                                                {renderAuthConfigField('scope', 'Scope', { required: true, placeholder: 'e.g. openid profile email' })}

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        flexWrap: 'wrap',
                                                        gap: 1.5,
                                                        p: 1.5,
                                                        mb: 2,
                                                        borderRadius: 1,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        bgcolor: 'action.hover',
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            icon={isOAuthConnected ? <CheckCircle /> : undefined}
                                                            label={isOAuthConnected ? 'Connected' : 'Not Connected'}
                                                            color={isOAuthConnected ? 'success' : 'default'}
                                                            variant={isOAuthConnected ? 'filled' : 'outlined'}
                                                            size="small"
                                                        />
                                                        {isOAuthConnected && authConfig.connected_at && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Connected at {new Date(authConfig.connected_at).toLocaleString()}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        {!isOAuthConnected && isOAuth2AuthFilled && (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={oauthActionLoading ? <CircularProgress size={16} color="inherit" /> : <Launch fontSize="small" />}
                                                                onClick={handleConnectOAuth}
                                                                disabled={oauthActionLoading || loading}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Connect with OAuth
                                                            </Button>
                                                        )}

                                                        {isOAuthConnected && (
                                                            <>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={oauthActionLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
                                                                    onClick={handleReconnectOAuth}
                                                                    disabled={oauthActionLoading || loading}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Reconnect
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    startIcon={oauthActionLoading ? <CircularProgress size={16} color="inherit" /> : <LinkOff fontSize="small" />}
                                                                    onClick={handleDisconnectOAuth}
                                                                    disabled={oauthActionLoading || loading}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Disconnect
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    // MCP Configuration
                                    <>
                                        <TextField
                                            fullWidth
                                            label="MCP Server URL *"
                                            value={mcpServerUrl}
                                            onChange={(e) => handleMcpServerUrlChange(e.target.value)}
                                            onBlur={(e) => {
                                                handleUrlBlur(e.target.value);
                                                checkDcrSupportForUrl(e.target.value);
                                            }}
                                            placeholder="https://mcp.example.com"
                                            sx={{ mb: 2 }}
                                            required
                                            helperText={
                                                dcrChecking
                                                    ? 'Checking MCP server for OAuth 2.1 (DCR) support...'
                                                    : mcpSupportsDcr === true
                                                    ? 'OAuth 2.1 (DCR) supported.'
                                                    : mcpSupportsDcr === false
                                                    ? 'Standard MCP server (configure transport & authentication below).'
                                                    : ''
                                            }
                                            InputProps={{
                                                endAdornment: dcrChecking ? <CircularProgress size={20} /> : null
                                            }}
                                        />

                                        {mcpSupportsDcr === true && (
                                            <Box
                                                sx={{
                                                    mb: 3,
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        mb: 1.5,
                                                        flexWrap: 'wrap',
                                                        gap: 1
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        OAuth 2.1 Connection (DCR)
                                                    </Typography>
                                                    {isOAuthConnected && authConfig.connected_at ? (
                                                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                                            Connected at {new Date(authConfig.connected_at).toLocaleString()}
                                                        </Typography>
                                                    ) : isDcrRegistered ? (
                                                        <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 500 }}>
                                                            Client registered
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Not connected
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    {!isDcrRegistered && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={dcrLoading ? <CircularProgress size={16} color="inherit" /> : <Launch fontSize="small" />}
                                                            onClick={handleConnectAppDcr}
                                                            disabled={dcrLoading || loading || !mcpServerUrl}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            {dcrLoading ? 'Connecting App...' : 'Connect App'}
                                                        </Button>
                                                    )}

                                                    {isDcrRegistered && !isOAuthConnected && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={oauthActionLoading ? <CircularProgress size={16} color="inherit" /> : <Launch fontSize="small" />}
                                                            onClick={handleConnectOAuth}
                                                            disabled={oauthActionLoading || loading}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            Connect with OAuth
                                                        </Button>
                                                    )}

                                                    {isDcrRegistered && isOAuthConnected && (
                                                        <>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={oauthActionLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
                                                                onClick={handleReconnectOAuth}
                                                                disabled={oauthActionLoading || loading}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Reconnect
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                size="small"
                                                                startIcon={oauthActionLoading ? <CircularProgress size={16} color="inherit" /> : <LinkOff fontSize="small" />}
                                                                onClick={handleDisconnectOAuth}
                                                                disabled={oauthActionLoading || loading}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Disconnect
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        {mcpSupportsDcr === false && (
                                            <>
                                                <FormControl fullWidth sx={{ mb: 2 }}>
                                                    <InputLabel>Transport</InputLabel>
                                                    <Select
                                                        value={mcpTransport}
                                                        label="Transport"
                                                        onChange={(e) => setMcpTransport(e.target.value)}
                                                    >
                                                        <MenuItem value="http">HTTP</MenuItem>
                                                        <MenuItem value="stdio">STDIO</MenuItem>
                                                    </Select>
                                                </FormControl>

                                                {mcpTransport === 'http' ? (
                                                    <>
                                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Headers</Typography>
                                                        {mcpHeaders.map((header, idx) => (
                                                            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                                <TextField
                                                                    size="small"
                                                                    placeholder="Header Name (e.g., Authorization)"
                                                                    value={header.key}
                                                                    onChange={(e) => {
                                                                        const newHeaders = [...mcpHeaders];
                                                                        newHeaders[idx].key = e.target.value;
                                                                        setMcpHeaders(newHeaders);
                                                                    }}
                                                                />
                                                                <TextField
                                                                    size="small"
                                                                    placeholder="Value"
                                                                    value={header.value}
                                                                    onChange={(e) => {
                                                                        const newHeaders = [...mcpHeaders];
                                                                        newHeaders[idx].value = e.target.value;
                                                                        setMcpHeaders(newHeaders);
                                                                    }}
                                                                    fullWidth
                                                                />
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        const newHeaders = mcpHeaders.filter((_, i) => i !== idx);
                                                                        setMcpHeaders(newHeaders);
                                                                    }}
                                                                >
                                                                    <Close />
                                                                </IconButton>
                                                            </Box>
                                                        ))}
                                                        <Button
                                                            size="small"
                                                            startIcon={<Add />}
                                                            onClick={() => setMcpHeaders([...mcpHeaders, { key: '', value: '' }])}
                                                            sx={{ mb: 3 }}
                                                        >
                                                            Add Header
                                                        </Button>
                                                    </>
                                                ) : (
                                            <>
                                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Command *"
                                                        value={mcpCommand}
                                                        onChange={(e) => setMcpCommand(e.target.value)}
                                                        placeholder="npx"
                                                        required
                                                    />
                                                </Box>

                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Arguments</Typography>
                                                {mcpArgs.map((arg, idx) => (
                                                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            value={arg}
                                                            onChange={(e) => {
                                                                const newArgs = [...mcpArgs];
                                                                newArgs[idx] = e.target.value;
                                                                setMcpArgs(newArgs);
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newArgs = mcpArgs.filter((_, i) => i !== idx);
                                                                setMcpArgs(newArgs);
                                                            }}
                                                        >
                                                            <Close />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Button
                                                    size="small"
                                                    startIcon={<Add />}
                                                    onClick={() => setMcpArgs([...mcpArgs, ''])}
                                                    sx={{ mb: 3 }}
                                                >
                                                    Add Argument
                                                </Button>

                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Environment Variables</Typography>
                                                {mcpEnv.map((env, idx) => (
                                                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                        <TextField
                                                            size="small"
                                                            placeholder="KEY"
                                                            value={env.key}
                                                            onChange={(e) => {
                                                                const newEnv = [...mcpEnv];
                                                                newEnv[idx].key = e.target.value;
                                                                setMcpEnv(newEnv);
                                                            }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            placeholder="VALUE"
                                                            value={env.value}
                                                            onChange={(e) => {
                                                                const newEnv = [...mcpEnv];
                                                                newEnv[idx].value = e.target.value;
                                                                setMcpEnv(newEnv);
                                                            }}
                                                            fullWidth
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                const newEnv = mcpEnv.filter((_, i) => i !== idx);
                                                                setMcpEnv(newEnv);
                                                            }}
                                                        >
                                                            <Close />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Button
                                                    size="small"
                                                    startIcon={<Add />}
                                                    onClick={() => setMcpEnv([...mcpEnv, { key: '', value: '' }])}
                                                    sx={{ mb: 3 }}
                                                >
                                                    Add Env Var
                                                </Button>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                                {/* Parameters Section */}
                                <Box sx={{ mb: 3, mt: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Global Parameters</Typography>
                                        <IconButton size="small" onClick={handleAddParameter} color="primary">
                                            <Add />
                                        </IconButton>
                                    </Box>

                                    {parameters.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                                            No global parameters defined.
                                        </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {parameters.map((param, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <FormControl size="small" sx={{ flex: 1 }}>
                                                        <Select
                                                            value={param.location}
                                                            onChange={(e) => handleParameterChange(idx, 'location', e.target.value)}
                                                            displayEmpty
                                                        >
                                                            {PARAM_LOCATIONS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                                                        </Select>
                                                    </FormControl>
                                                    <FormControl size="small" sx={{ flex: 1 }}>
                                                        <Select
                                                            value={param.type}
                                                            onChange={(e) => handleParameterChange(idx, 'type', e.target.value)}
                                                            displayEmpty
                                                        >
                                                            {PARAM_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                                        </Select>
                                                    </FormControl>
                                                    <IconButton size="small" color="error" onClick={() => handleDeleteParameter(idx)}>
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <TextField
                                                        size="small"
                                                        placeholder="Key"
                                                        value={param.name || ''}
                                                        onChange={(e) => handleParameterChange(idx, 'name', e.target.value)}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        placeholder="Value"
                                                        value={param.value || ''}
                                                        onChange={(e) => handleParameterChange(idx, 'value', e.target.value)}
                                                        sx={{ flex: 1 }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {apiType === 'MCP' && (mcpSupportsDcr === false || (mcpSupportsDcr === true && isOAuthConnected)) && (
                                    <Button
                                        variant="outlined"
                                        startIcon={fetchLoading ? <CircularProgress size={20} /> : <Refresh />}
                                        onClick={handleFetchMcpTools}
                                        disabled={fetchLoading}
                                        fullWidth
                                        sx={{ mb: 3 }}
                                    >
                                        {fetchLoading ? 'Connecting...' : (mcpSupportsDcr ? 'Re-fetch Tools' : 'Connect & Fetch Tools')}
                                    </Button>
                                )}

                            </Box>

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {apiType === 'MCP' ? 'Tools' : 'Actions'}
                                        </Typography>
                                        <Tooltip
                                            arrow
                                            placement="right"
                                            componentsProps={{ tooltip: { sx: { maxWidth: 460 } } }}
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                                                        How to define an action
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                                        Each action is one API endpoint. Use the <b>Preview</b> builder, or switch to <b>JSON</b> to paste an array of action objects.
                                                    </Typography>
                                                    <Box
                                                        component="pre"
                                                        sx={{ m: 0, mb: 1, p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.35)', fontSize: 11, lineHeight: 1.45, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                                                    >{`[
  {
    "name": "upload_and_attach",
    "method": "POST",
    "path": "/path?script=1426",
    "description": "What this action does",
    "send_attachment": true,
    "attachment_content_field": "fileContent",
    "attachment_name_field": "fileName",
    "attachment_file_type": "application/pdf",
    "attachment_folder_id": "123",
    "parameters": {
      "type": "object",
      "properties": {
        "recordId": { "type": "string", "description": "..." }
      },
      "required": ["recordId"]
    }
  }
]`}</Box>
                                                    <Typography variant="caption" component="div" sx={{ display: 'block' }}>
                                                        • <b>name / method / path</b> — required. Path params use <code>{'{id}'}</code> form.<br />
                                                        • <b>parameters</b> — JSON-Schema object (above) <i>or</i> a flat array of <code>{'{ name, type, location, required, description }'}</code>. Both are accepted; the builder normalizes to the array form.<br />
                                                        • <b>send_attachment</b> — set <code>true</code> to forward the request's uploaded file as multipart form-data.<br />
                                                        • <b>attachment_* fields</b> — optional, only used when <code>send_attachment</code> is true.
                                                    </Typography>
                                                </Box>
                                            }
                                        >
                                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                    {apiType !== 'MCP' && (
                                        <ToggleButtonGroup
                                            size="small"
                                            value={useActionBuilder ? 'preview' : 'json'}
                                            exclusive
                                            onChange={(e, newMode) => {
                                                if (newMode !== null) {
                                                    const isPreview = newMode === 'preview';
                                                    if (useActionBuilder !== isPreview) {
                                                        if (useActionBuilder) {
                                                            setActionsJson(JSON.stringify(parsedActions, null, 2));
                                                        } else {
                                                            try {
                                                                setParsedActions(normalizeActions(JSON.parse(actionsJson)));
                                                            } catch (err) { /* ignore invalid json */ }
                                                        }
                                                        setUseActionBuilder(isPreview);
                                                    }
                                                }
                                            }}
                                            aria-label="editor mode"
                                            sx={{ height: 32 }}
                                        >
                                            <ToggleButton value="preview" aria-label="preview mode" sx={{ px: 2, textTransform: 'none' }}>
                                                <ViewList fontSize="small" sx={{ mr: 1 }} /> Preview
                                            </ToggleButton>
                                            <ToggleButton value="json" aria-label="json mode" sx={{ px: 2, textTransform: 'none' }}>
                                                <Code fontSize="small" sx={{ mr: 1 }} /> JSON
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    )}
                                </Box>

                                {useActionBuilder ? (
                                    <ActionBuilder
                                        actions={parsedActions}
                                        onChange={setParsedActions}
                                        readOnly={apiType === 'MCP'}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        multiline
                                        value={actionsJson}
                                        onChange={(e) => setActionsJson(e.target.value)}
                                        placeholder="[ { 'name': 'action1', ... } ]"
                                        sx={{
                                            flexGrow: 1,
                                            '& .MuiInputBase-root': {
                                                height: '100%',
                                                fontFamily: 'monospace',
                                                alignItems: 'flex-start'
                                            },
                                            '& .MuiInputBase-input': {
                                                height: '100% !important',
                                                overflow: 'auto !important'
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    {!fetchingData && activeTab === 1 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Test Input (JSON)
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={5}
                                value={testInput}
                                onChange={(e) => setTestInput(e.target.value)}
                                placeholder="{ 'action': 'get_user', 'params': { 'id': '123' } }"
                                sx={{ fontFamily: 'monospace', mb: 3 }}
                            />

                            <Button
                                variant="contained"
                                onClick={handleTest}
                                disabled={testLoading}
                                startIcon={testLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                            >
                                Run Test
                            </Button>

                            {testResult && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Test Result
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            p: 2,
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            overflow: 'auto',
                                            maxHeight: 300
                                        }}
                                    >
                                        {JSON.stringify(testResult, null, 2)}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className={styles.dialogActions}>
                    <Button onClick={handleDialogClose} disabled={loading}>
                        Cancel
                    </Button>
                    {!fetchingData && activeTab === 0 && (
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : (editTool ? 'Update' : 'Create')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={handleCloseToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
};

export default CreateCustomToolDialog;
